import { useEffect, useState, type ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  makeStyles,
  tokens,
  Title2,
  Body1,
  Caption1,
  Button,
  Spinner,
} from '@fluentui/react-components';
import {
  ShieldCheckmark24Regular,
  Person24Regular,
  ShieldProhibited24Regular,
  Phone24Regular,
} from '@fluentui/react-icons';
import { useAppContext } from '@/store/appContext';
import { useAuth } from '@/hooks/useAuth';
import { useSharePointList } from '@/hooks/useSharePointList';
import {
  biometricStatus,
  biometricVerify,
  isNativeShell,
  secureGet,
  secureSet,
} from '@/utils/nativeBridge';
import { enrollOrTouchDevice } from '@/graph/deviceEnrollment';
import type { DeviceStatus } from '@/models/types';
import { brand, glass } from '@/theme/prismTheme';

interface MemberAllowEntry {
  Title: string;
  Email: string;
  Status: 'Active' | 'Revoked';
  Notes: string;
}

const BIO_REMEMBER_KEY = 'prism_bio_verified_at';
const BIO_REVERIFY_AFTER_HOURS = 12;

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#FAFBFC',
    backgroundImage: brand.aurora,
    padding: '24px',
    textAlign: 'center' as const,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    padding: '40px 32px',
    background: glass.surfaceStrong.background,
    backdropFilter: glass.surfaceStrong.backdropFilter,
    WebkitBackdropFilter: glass.surfaceStrong.WebkitBackdropFilter,
    border: glass.surfaceStrong.border,
    boxShadow: glass.surfaceStrong.boxShadow,
    borderRadius: '24px',
    maxWidth: '420px',
    width: '100%',
  },
  iconOk: { fontSize: '56px', color: brand.blue },
  iconBlock: { fontSize: '56px', color: tokens.colorPaletteRedForeground1 },
  iconWarn: { fontSize: '56px', color: brand.orange },
  metaList: {
    width: '100%',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    background: 'rgba(0,0,0,0.03)',
    borderRadius: '10px',
    padding: '10px 14px',
    textAlign: 'left' as const,
    lineHeight: 1.6,
  },
});

interface AccessGateProps {
  children: ReactNode;
}

/**
 * Multi-layer access gate (each layer fails closed):
 *   1. App init complete
 *   2. MFA satisfied at Entra ID (amr claim contains 'mfa' / 'pwd' is NOT enough)
 *   3. Email allowlist (OC_AppAccessList, Status=Active)
 *   4. Device enrollment status (OC_Devices, Status=Approved). Pending devices
 *      see a "waiting for admin" screen; Blocked devices are hard-rejected.
 *   5. Biometric (native only) — Face ID / Touch ID, remembered for 12h.
 *
 * Admins (BOOTSTRAP list / 'Admin' role) bypass allowlist + device gates so
 * they can never lock themselves out.
 */
export function AccessGate({ children }: AccessGateProps) {
  const styles = useStyles();
  const { instance } = useMsal();
  const { graphClient, currentUser, isInitialized, listIds } = useAppContext();
  const { logout } = useAuth();

  const { items: allowList, loading: listLoading } =
    useSharePointList<MemberAllowEntry>('OC_AppAccessList', {
      autoFetch: !!listIds,
    });

  const [bioState, setBioState] = useState<
    'checking' | 'pending' | 'verified' | 'unsupported'
  >('checking');
  const [bioReason, setBioReason] = useState<string>('');

  const [deviceState, setDeviceState] = useState<
    | { kind: 'checking' }
    | { kind: 'unconfigured' } // OC_Devices list missing — feature off
    | { kind: 'enrolled'; status: DeviceStatus; model: string }
    | { kind: 'error'; reason: string }
  >({ kind: 'checking' });

  // ── Layer 1: Init guard ─────────────────────────────────────────────────
  // (handled below in render flow)

  // ── Layer 2: MFA claim from id token ────────────────────────────────────
  // Entra ID v1.0 tokens include an `amr` array of auth methods satisfied
  // for the session. v2.0 tokens (modern SPA default) often *omit* `amr`
  // unless the app reg explicitly opts in via optional claims. We treat the
  // absence of `amr` as "trust the tenant" — Conditional Access / Security
  // Defaults are the authoritative MFA enforcement; this app-side check is
  // defense-in-depth only.
  //
  // Real-world claims observed:
  //   `amr=["pwd","mfa"]` → MFA done, allow
  //   `amr=["mfa"]`       → MFA done, allow
  //   `amr=["wia"]`       → managed device w/ Hello-for-Business, allow
  //   `amr=["fido"]`      → security key, allow
  //   `amr=["pwd"]`       → password only, BLOCK
  //   `amr` missing       → v2.0 token, trust tenant policy → allow
  //   `acr="1"`           → legacy MFA claim, allow
  const account = instance.getActiveAccount();
  const idClaims = account?.idTokenClaims as
    | Record<string, unknown>
    | undefined;
  const amr = idClaims?.amr as string[] | undefined;
  const acr = idClaims?.acr as string | undefined;
  const mfaSatisfied = (() => {
    // Strong signal: amr explicitly contains an MFA factor
    if (amr?.some((m) => ['mfa', 'wia', 'fido', 'rsa', 'otp', 'sms'].includes(m))) {
      return true;
    }
    // Legacy v1.0 acr claim ("0" = no MFA, "1" = MFA done)
    if (acr === '1') return true;
    // amr present but only password-level → genuine MFA failure, block
    if (amr && amr.length > 0 && amr.every((m) => m === 'pwd')) return false;
    // Default: trust the tenant. If tenant CA / Security Defaults requires
    // MFA, Entra wouldn't have issued this token without it.
    return true;
  })();

  // ── Layer 3: Email allowlist ────────────────────────────────────────────
  const isAdmin = currentUser?.role === 'Admin';
  const userEmail = currentUser?.email?.toLowerCase() ?? '';
  const isAllowed = (() => {
    if (isAdmin) return true; // Admins can never be locked out
    if (!listIds) return null;
    if (listLoading) return null;
    return allowList.some(
      (e) =>
        e.fields.Status === 'Active' &&
        e.fields.Email?.toLowerCase() === userEmail,
    );
  })();

  // ── Layer 4: Device enrollment ──────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!graphClient || !listIds || !currentUser) return;
      try {
        const enrolled = await enrollOrTouchDevice(
          graphClient,
          listIds,
          currentUser.email,
          currentUser.displayName,
        );
        if (cancelled) return;
        if (!enrolled) {
          // OC_Devices list not provisioned — feature is off
          setDeviceState({ kind: 'unconfigured' });
          return;
        }
        setDeviceState({
          kind: 'enrolled',
          status: enrolled.fields.Status ?? 'Pending',
          model: enrolled.fields.Model || 'this device',
        });
      } catch (err) {
        console.error('[Prism] Device enrollment failed:', err);
        if (!cancelled)
          setDeviceState({
            kind: 'error',
            reason: err instanceof Error ? err.message : 'Unknown error',
          });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [graphClient, listIds, currentUser]);

  // ── Layer 5: Biometric ──────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isNativeShell()) {
        if (!cancelled) setBioState('unsupported');
        return;
      }
      const status = await biometricStatus();
      if (!status.available) {
        if (!cancelled) setBioState('unsupported');
        return;
      }
      const lastVerifiedRaw = await secureGet(BIO_REMEMBER_KEY);
      const lastVerified = lastVerifiedRaw ? Number(lastVerifiedRaw) : 0;
      const expiresAt = lastVerified + BIO_REVERIFY_AFTER_HOURS * 3600 * 1000;
      if (Date.now() < expiresAt) {
        if (!cancelled) setBioState('verified');
        return;
      }
      if (!cancelled) setBioState('pending');
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const promptBio = async () => {
    const ok = await biometricVerify("Verify it's you to open Prism");
    if (ok) {
      await secureSet(BIO_REMEMBER_KEY, String(Date.now()));
      setBioState('verified');
    } else {
      setBioReason('Verification failed or cancelled. Try again.');
    }
  };

  // ── Render flow ────────────────────────────────────────────────────────

  if (!isInitialized) {
    return (
      <div className={styles.page}>
        <Spinner size="large" label="Initializing Prism..." />
      </div>
    );
  }

  // MFA check (skip if no account — shouldn't happen inside AuthenticatedTemplate)
  if (account && !mfaSatisfied) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <ShieldProhibited24Regular className={styles.iconBlock} />
          <Title2>MFA required</Title2>
          <Body1>
            Prism requires multi-factor authentication. Your current session
            was authenticated with{' '}
            <strong>{amr && amr.length > 0 ? amr.join(', ') : 'password only'}</strong>.
          </Body1>
          <Caption1>
            Sign out, sign back in, and complete the Microsoft Authenticator
            challenge.
          </Caption1>
          <Button appearance="primary" onClick={logout}>
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  if (isAllowed === null) {
    return (
      <div className={styles.page}>
        <Spinner size="large" label="Checking your access..." />
      </div>
    );
  }

  if (!isAllowed) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <ShieldProhibited24Regular className={styles.iconBlock} />
          <Title2>Access Pending</Title2>
          <Body1>
            Your account ({userEmail}) is signed in but hasn't been added to
            the Prism allow list yet.
          </Body1>
          <Caption1>
            Ask a Prism administrator to add you under{' '}
            <strong>Settings → Member Access</strong>.
          </Caption1>
          <Button appearance="outline" onClick={logout}>
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  // Device enforcement (admins bypass)
  if (!isAdmin && deviceState.kind === 'enrolled') {
    if (deviceState.status === 'Blocked') {
      return (
        <div className={styles.page}>
          <div className={styles.card}>
            <ShieldProhibited24Regular className={styles.iconBlock} />
            <Title2>Device blocked</Title2>
            <Body1>
              An administrator has blocked {deviceState.model} from accessing
              Prism.
            </Body1>
            <Caption1>
              If you think this is wrong, ask an admin to review under{' '}
              <strong>Settings → Devices</strong>.
            </Caption1>
            <Button appearance="outline" onClick={logout}>
              Sign out
            </Button>
          </div>
        </div>
      );
    }
    if (deviceState.status === 'Pending') {
      return (
        <div className={styles.page}>
          <div className={styles.card}>
            <Phone24Regular className={styles.iconWarn} />
            <Title2>Approval required</Title2>
            <Body1>
              We've registered {deviceState.model} for {currentUser?.email} and
              notified your admin.
            </Body1>
            <Caption1>
              Once they approve this install in{' '}
              <strong>Settings → Devices</strong>, sign out and back in to
              continue.
            </Caption1>
            <Button appearance="primary" onClick={logout}>
              Sign out
            </Button>
          </div>
        </div>
      );
    }
  }

  if (bioState === 'pending') {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <Person24Regular className={styles.iconOk} />
          <Title2>Welcome back</Title2>
          <Body1>{currentUser?.displayName}</Body1>
          <Caption1>
            For your security, verify it's you with Face ID / Touch ID.
          </Caption1>
          {bioReason && (
            <Caption1 style={{ color: tokens.colorPaletteRedForeground1 }}>
              {bioReason}
            </Caption1>
          )}
          <Button
            appearance="primary"
            size="large"
            icon={<ShieldCheckmark24Regular />}
            onClick={promptBio}
          >
            Unlock with Face ID
          </Button>
          <Button appearance="subtle" onClick={logout}>
            Sign out instead
          </Button>
        </div>
      </div>
    );
  }

  // Allowed + (verified OR unsupported) — let them in
  return <>{children}</>;
}
